/**
 * DatePicker helpers — Phase 6a.9c.
 *
 * Pure string converters between the auditor-friendly display format
 * `DD.MM.YYYY` (Turkish standard, used in both TR and EN UI modes per
 * design directive) and ISO 8601 `YYYY-MM-DD` (the canonical storage
 * format used by the form, Zod schemas, localStorage entries, and the
 * backend payload).
 *
 * Both helpers are strict:
 *   • `parseDisplayToIso` rejects any non-canonical separator, missing
 *     leading zeros, or calendar-invalid dates (e.g. 31.02.2025) via a
 *     round-trip refine — mirrors backend Finding J's isoDate Zod refine
 *     pattern so we never let `new Date()` silently roll over.
 *   • `formatIsoToDisplay` rejects malformed ISO strings rather than
 *     emitting garbage; consumers receive an empty string and the input
 *     simply renders empty.
 */

/**
 * Parse `"DD.MM.YYYY"` → ISO `"YYYY-MM-DD"`.
 *
 * Returns `null` when the input:
 *   • is empty / not exactly 10 chars
 *   • uses the wrong separator (`-`, `/`, space, …)
 *   • lacks leading zeros (`1.5.2025`)
 *   • contains non-numeric pieces
 *   • represents a date that does not exist on the calendar
 *     (`31.02.2025`, `29.02.2025`, `99.99.9999`)
 */
export function parseDisplayToIso(text: string): string | null {
  if (typeof text !== "string") return null;
  // Strict: exactly DD.MM.YYYY (10 chars, dots as separators).
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  if (
    !Number.isInteger(day) ||
    !Number.isInteger(month) ||
    !Number.isInteger(year)
  ) {
    return null;
  }
  if (year < 1900 || year > 9999) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  // Round-trip refine: construct a UTC Date from the parts; if the Date's
  // components do not match the input (e.g. Feb 31 → Mar 3), reject. This
  // catches silent calendar rollover the same way the backend Finding J
  // fix did for the isoDate Zod schema.
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return null;
  }

  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/**
 * Format ISO `"YYYY-MM-DD"` → `"DD.MM.YYYY"`.
 *
 * Returns `""` when the input is empty or not a valid ISO date string.
 * Symmetric with `parseDisplayToIso` so a round-trip
 * `formatIsoToDisplay(parseDisplayToIso(x))` is the identity on well-
 * formed input.
 */
export function formatIsoToDisplay(iso: string): string {
  if (typeof iso !== "string" || iso === "") return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  // Round-trip refine to reject calendar-invalid ISO inputs.
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return "";
  }
  return `${match[3]}.${match[2]}.${match[1]}`;
}

/**
 * Convenience bridge for callers that store a JS `Date` object rather
 * than an ISO string (current `<DatePicker>` external API). Pure
 * function; returns `""` for `undefined`/invalid inputs so the input
 * can render empty.
 */
export function dateToDisplay(date: Date | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  const iso = dateToIso(date);
  return formatIsoToDisplay(iso);
}

/**
 * Convenience bridge: ISO string → `Date` (UTC midnight) or `undefined`.
 */
export function isoToDate(iso: string | null): Date | undefined {
  if (!iso) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return undefined;
  return new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
}

/** `Date` → ISO `"YYYY-MM-DD"` using UTC components. */
export function dateToIso(date: Date): string {
  const yyyy = String(date.getUTCFullYear()).padStart(4, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
